import { useDeepCompareEffect } from 'react-use';

import { customFieldsApi } from 'apis/customFields';
import {
  elevionCustomAttributePrefix,
  elevionSystemAttributePrefix,
  ElevionSystemAttributePrefix,
  elevionSystemAttributesDataType,
  ElevionSystemAttributeValue,
} from 'utils/elevion';

import {
  CustomField,
  UseCustomFieldsOptions,
} from '../useLazyCustomFields/useLazyCustomFields.types';

type StripSystemPrefix<T extends string> =
  T extends `${ElevionSystemAttributePrefix}${infer U}` ? U : never;

type ElevionSystemAttributeValueSimplified = {
  [K in StripSystemPrefix<
    keyof ElevionSystemAttributeValue
  >]: ElevionSystemAttributeValue[`${ElevionSystemAttributePrefix}${K}`]['value'];
};

/**
 * Checks if custom fields are visible based on their conditional visibility
 * status. It tracks the changes in the custom attributes and system context and
 * returns the new visibility status.
 *
 * @returns An object mapping custom field ids to their visibility status.
 */
export const useCustomFieldsVisibility = (
  customFields: readonly CustomField[],
  systemContext: ElevionSystemAttributeValueSimplified,
  options: Pick<UseCustomFieldsOptions, 'includeArchived'> = {},
): {
  data: Record<string, boolean>;
  isLoading: boolean;
  isFetching: boolean;
} => {
  const [readVisibility, visibilityResponse] =
    customFieldsApi.useLazyReadVisibilityQuery();

  useDeepCompareEffect(() => {
    const systemContextNormalized = getSystemContextNormalized(systemContext);

    if (customFields.length === 0) {
      return;
    }

    readVisibility({
      entityId: customFields[0].attribute.entity_id,
      params: { include_archived: options.includeArchived },
      context: customFields.reduce((context, customField) => {
        return {
          ...context,
          [`${elevionCustomAttributePrefix}${customField.attribute.identifier}`]:
            customField.value,
        };
      }, systemContextNormalized),
    });
  }, [customFields, systemContext]);

  return {
    data: visibilityResponse.data?.data ?? {},
    isLoading: visibilityResponse.isLoading,
    isFetching: visibilityResponse.isFetching,
  };
};

export function getSystemContextNormalized(
  systemContext: ElevionSystemAttributeValueSimplified,
): ElevionSystemAttributeValue {
  return Object.fromEntries(
    Object.entries(systemContext).map(([key, value]) => {
      const isEmptyQueue =
        key === 'queue' && value.length === 1 && value[0] === '';

      return [
        `${elevionSystemAttributePrefix}${key}`,
        {
          data_type: elevionSystemAttributesDataType[key],
          value: isEmptyQueue ? [] : value,
        },
      ];
    }),
  ) as unknown as ElevionSystemAttributeValue;
}
